<template>
  <div class="container homepage flex">
    <div class="bg-img">
      <div class="cloud">
        <img src="../assets/images/cloud.png" alt="bg-img" />
      </div>
      <div class="curveline">
        <img src="../assets/images/curveline.png" alt="bg-img" />
      </div>
      <div class="butterfly">
        <img src="../assets/images/butterfly.png" alt="bg-img" />
      </div>
      <div class="scale">
        <img src="../assets/images/scale.png" alt="bg-img" />
      </div>
      <div class="rainbow">
        <img src="../assets/images/rainbow.png" alt="bg-img" />
      </div>
    </div>
    <div class="form-container flex">
      <div class="logo">
        <img src="../assets/images/UP_SKOOL_LOGO.png" alt="" />
      </div>
      <div class="text-container">
        <h1>Create Account</h1>
        <p>Join Up-skool for free and start your learning journey</p>
      </div>
      <div class="inputs-container flex">
        <!-- Parent Name -->
        <div class="input-container">
          <span class="icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </span>
          <input
            v-model="signupData.name"
            type="text"
            name="name"
            id="name"
            placeholder="Your full name"
            :class="{ 'error': errors.name }"
          />
        </div>
        
        <!-- Mobile Number -->
        <div class="input-container">
          <span class="icon">
            <img src="../assets/images/smartphone.png" alt="" />
          </span>
          <input
            v-model="signupData.mobile"
            type="text"
            name="mobile"
            id="mobile"
            maxlength="10"
            placeholder="Mobile number (10 digits)"
            :class="{ 'error': errors.mobile }"
          />
        </div>
        
        <!-- Email (Optional) -->
        <div class="input-container">
          <span class="icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </span>
          <input
            v-model="signupData.email"
            type="email"
            name="email"
            id="email"
            placeholder="Email address (optional)"
            :class="{ 'error': errors.email }"
          />
        </div>
      </div>
      
      <!-- Terms and Conditions -->
      <div class="terms-container">
        <label class="checkbox-container">
          <input type="checkbox" v-model="agreeToTerms" />
          <span class="checkmark"></span>
          <span class="checkbox-text">
            I agree to the 
            <a href="https://up-skool.com/terms-of-use" target="_blank">Terms of Service</a> 
            and 
            <a href="https://up-skool.com/privacy-policy" target="_blank">Privacy Policy</a>
          </span>
        </label>
      </div>
      
      <!-- Error Messages -->
      <div v-if="Object.keys(errors).length > 0" class="error-messages">
        <p v-for="error in errors" :key="error" class="error-text">{{ error }}</p>
      </div>
      
      <div class="button-container flex">
        <div 
          class="button flex" 
          @click="handleSignup()"
          :class="{ 'disabled': !canSignup || isLoading }"
        >
          {{ isLoading ? 'Creating Account...' : 'Create Free Account' }}
        </div>
      </div>
      
      <!-- Login Link -->
      <div class="login-link">
        <p>Already have an account? <router-link to="/login">Sign In</router-link></p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "simple-signup",
  data() {
    return {
      isLoading: false,
      agreeToTerms: false,
      signupData: {
        name: "",
        mobile: "",
        email: "",
        type: "P", // Parent type
        country_code: "+91", // Default to India
      },
      errors: {}
    };
  },
  computed: {
    ...mapGetters(["isLoggedIn"]),
    canSignup() {
      return (
        this.signupData.name.trim().length >= 3 &&
        this.signupData.mobile.length === 10 &&
        this.agreeToTerms &&
        !this.isLoading
      );
    }
  },
  methods: {
    ...mapActions(["simpleSignupUser"]),
    
    validateForm() {
      this.errors = {};
      
      // Validate name
      if (!this.signupData.name.trim()) {
        this.errors.name = "Name is required";
      } else if (this.signupData.name.trim().length < 3) {
        this.errors.name = "Name must be at least 3 characters";
      }
      
      // Validate mobile
      if (!this.signupData.mobile) {
        this.errors.mobile = "Mobile number is required";
      } else if (!/^\d{10}$/.test(this.signupData.mobile)) {
        this.errors.mobile = "Mobile number must be 10 digits";
      }
      
      // Validate email (if provided)
      if (this.signupData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.signupData.email)) {
        this.errors.email = "Please enter a valid email address";
      }
      
      // Validate terms
      if (!this.agreeToTerms) {
        this.errors.terms = "You must agree to the Terms of Service";
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async handleSignup() {
      if (!this.validateForm()) {
        return;
      }
      
      this.isLoading = true;
      
      try {
        await this.simpleSignupUser(this.signupData);
        
        this.$buefy.toast.open({
          message: "Account created successfully! Please login to continue.",
          type: "is-success",
          duration: 4000
        });
        
        // Redirect to login page
        this.$router.push({
          name: "login",
          query: { mobile: this.signupData.mobile }
        });
        
      } catch (error) {
        console.error("Signup error:", error);
        
        const errorMessage = error.response?.data?.msg || 
                           error.response?.data || 
                           "Something went wrong. Please try again.";
        
        this.$buefy.toast.open({
          message: errorMessage,
          type: "is-danger",
          duration: 4000
        });
      } finally {
        this.isLoading = false;
      }
    }
  },
  created() {
    // Redirect if already logged in
    if (this.isLoggedIn) {
      this.$router.push({ name: "home" });
      return;
    }
  }
};
</script>

<style scoped>
/* Ensure full viewport coverage */
* {
  box-sizing: border-box;
}

.flex {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.container {
  position: relative;
}

.homepage {
  min-height: 100vh;
  width: 100%;
  padding: 1rem;
}

.bg-img {
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
}

.bg-img div {
  display: flex;
  position: absolute;
  width: auto;
}

.cloud {
  top: 5%;
  right: 10%;
  width: 15%;
}

.curveline {
  top: 20%;
  left: 5%;
  width: 12%;
}

.butterfly {
  top: 10%;
  left: 20%;
  width: 8%;
}

.scale {
  bottom: 20%;
  right: 15%;
  width: 10%;
}

.rainbow {
  bottom: 10%;
  left: 10%;
  width: 20%;
}

.form-container {
  padding: 2rem;
  gap: 1.5rem;
  width: 100%;
  max-width: 500px;
  position: relative;
  margin: auto;
  top: 5%;
}

.logo img {
  width: 120px;
  height: auto;
}

.text-container {
  text-align: center;
}

.text-container h1 {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.5rem;
}

.text-container p {
  color: #666;
  font-size: 1rem;
}

.inputs-container {
  width: 100%;
  gap: 1rem;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.input-container:focus-within {
  border-color: #8919e7;
  box-shadow: 0 4px 15px rgba(137, 25, 231, 0.2);
}

.input-container .icon {
  margin-right: 0.75rem;
  width: 20px;
  height: 20px;
  opacity: 0.6;
}

.input-container .icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.input-container .icon svg {
  width: 20px;
  height: 20px;
  stroke: #666;
}

.input-container input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1rem;
  background: transparent;
}

.input-container input::placeholder {
  color: #999;
}

.input-container.error,
.input-container input.error {
  border-color: #ff3860;
}

.terms-container {
  width: 100%;
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1.4;
}

.checkbox-container input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin-top: 2px;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark {
  background-color: #8919e7;
  border-color: #8919e7;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark:after {
  content: "✓";
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.checkbox-text a {
  color: #8919e7;
  text-decoration: none;
}

.checkbox-text a:hover {
  text-decoration: underline;
}

.error-messages {
  width: 100%;
}

.error-text {
  color: #ff3860;
  font-size: 0.9rem;
  margin: 0.25rem 0;
}

.button-container {
  width: 100%;
}

.button {
  flex-direction: row;
  padding: 1rem 1.5rem;
  width: 100%;
  background: #8919e7;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
  position: relative;
  text-transform: capitalize;
  font-weight: bold;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  border: none;
  outline: none;
}

.button:hover:not(.disabled) {
  background: #7414d1;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(137, 25, 231, 0.3);
}

.button.disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.login-link {
  text-align: center;
  margin-top: 1rem;
}

.login-link p {
  color: #666;
  font-size: 0.9rem;
}

.login-link a {
  color: #8919e7;
  text-decoration: none;
  font-weight: bold;
}

.login-link a:hover {
  text-decoration: underline;
}

/* Responsive Design */
@media screen and (max-width: 1023px) {
  .text-container h1 {
    font-size: 1.75rem;
  }
}

@media screen and (max-width: 767px) {
  .text-container h1 {
    font-size: 1.5rem;
  }
  
  .bg-img div {
    display: none;
  }
}

@media screen and (max-width: 480px) {
  .form-container {
    padding: 1rem;
    top: 2%;
  }
  
  .text-container h1 {
    font-size: 1.25rem;
  }
  
  .input-container {
    padding: 0.6rem 0.8rem;
  }
  
  .button {
    padding: 0.875rem 1.25rem;
    font-size: 1rem;
  }
}
</style>